import React, { useState, useEffect, useContext } from 'react';
import { X, Gift } from 'lucide-react';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;
const DISMISS_KEY = 'exitPopupDismissed';
const DISMISS_DAYS = 7;

const translations = {
  en: {
    title: 'Wait! Before You Go...',
    subtitle: 'Get 10% off your first order',
    desc: 'Join our collectors community and receive exclusive deals on rare stamps.',
    placeholder: 'Enter your email',
    cta: 'Get My Discount',
    noThanks: 'No thanks, I\'ll pay full price',
    success: 'Welcome! Check your inbox for your discount code.',
  },
  tr: {
    title: 'Bekleyin! Gitmeden Önce...',
    subtitle: 'İlk siparişinizde %10 indirim',
    desc: 'Koleksiyoner topluluğumuza katılın ve nadir pullarda özel fırsatlar kazanın.',
    placeholder: 'E-posta adresiniz',
    cta: 'İndirimi Al',
    noThanks: 'Hayır teşekkürler',
    success: 'Hoş geldiniz! İndirim kodunuz e-postanıza gönderildi.',
  },
  el: {
    title: 'Περιμένετε! Πριν Φύγετε...',
    subtitle: '10% έκπτωση στην πρώτη παραγγελία',
    desc: 'Εγγραφείτε στην κοινότητα συλλεκτών μας.',
    placeholder: 'Email σας',
    cta: 'Πάρτε την Έκπτωση',
    noThanks: 'Όχι ευχαριστώ',
    success: 'Καλωσήρθατε! Ελέγξτε τα εισερχόμενά σας.',
  },
  de: {
    title: 'Warten Sie! Bevor Sie gehen...',
    subtitle: '10% Rabatt auf Ihre erste Bestellung',
    desc: 'Treten Sie unserer Sammlergemeinschaft bei.',
    placeholder: 'Ihre E-Mail',
    cta: 'Rabatt sichern',
    noThanks: 'Nein danke',
    success: 'Willkommen! Prüfen Sie Ihren Posteingang.',
  },
};

const ExitIntentPopup = () => {
  const { language } = useContext(AppContext);
  const t = translations[language] || translations.en;
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - parseInt(dismissed, 10) < DISMISS_DAYS * 86400000) return;

    let triggered = false;
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !triggered) {
        triggered = true;
        setVisible(true);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/collectors/newsletter`, { email });
      toast.success(t.success);
      dismiss();
    } catch {
      toast.error('Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[300] animate-fadeIn" onClick={dismiss} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[301] w-full max-w-md mx-4 animate-scaleIn">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#1F2D50] to-[#2a3d6b] p-6 text-center text-white relative">
            <button onClick={dismiss} className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded transition" aria-label="Close">
              <X size={20} />
            </button>
            <Gift size={40} className="mx-auto mb-3 text-[#C8A951]" />
            <h2 className="text-2xl font-bold mb-1">{t.title}</h2>
            <p className="text-[#C8A951] font-semibold text-lg">{t.subtitle}</p>
          </div>
          <div className="p-6">
            <p className="text-gray-600 text-sm text-center mb-4">{t.desc}</p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.placeholder}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#C8A951] focus:ring-0 outline-none transition"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#C8A951] hover:bg-[#b89842] text-white py-3 rounded-lg font-semibold transition disabled:opacity-50"
              >
                {submitting ? '...' : t.cta}
              </button>
            </form>
            <button onClick={dismiss} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-3 transition">
              {t.noThanks}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExitIntentPopup;
